import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
  TooltipProps,
  ReferenceLine,
  Legend,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { useTheme } from "@/context/ThemeContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  NewCustomerReplacementEfficiencyDataItem,
  NewCustomerReplacementEfficiencyByMonthData,
} from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchStochasticDashboardData/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { generateColors } from "@/modules/stochastic/features/Reports/SCDashboard/utils/chart";
import { SlidingToggleGroup } from "@/modules/stochastic/features/Reports/SCDashboard/components/SlidingToggleGroup/SlidingToggleGroup";

type ViewMode = "yearly" | "monthly";

type Props = {
  initialData?: NewCustomerReplacementEfficiencyDataItem[];
  monthlyData?: NewCustomerReplacementEfficiencyByMonthData;
};

const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  const { theme } = useTheme();

  if (active && payload && payload.length) {
    const data = payload[0]?.payload;

    const styles = {
      backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
      borderColor: theme === "dark" ? "#374151" : "#E5E7EB",
      color: theme === "dark" ? "#F3F4F6" : "#1F2937",
    };

    return (
      <div
        className="p-3 border rounded text-sm shadow-lg space-y-2 max-w-xs"
        style={styles}
      >
        <div className="font-semibold">Year: {label}</div>
        <div className="space-y-1">
          <div>
            <span className="font-medium">Efficiency Score:</span>{" "}
            <span className="font-bold text-blue-600">{data.efficiency}</span>
          </div>
          <div>
            <span className="font-medium">Quintile:</span>{" "}
            <span
              className={`font-bold ${
                data.quintileGrade === "A"
                  ? "text-green-600"
                  : data.quintileGrade === "B"
                    ? "text-blue-600"
                    : data.quintileGrade === "C"
                      ? "text-yellow-600"
                      : data.quintileGrade === "D"
                        ? "text-orange-600"
                        : data.quintileGrade === "F"
                          ? "text-red-600"
                          : "text-gray-600"
              }`}
            >
              {data.quintileGrade}
            </span>
          </div>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <div>Non-Replacement Calls: {data.nonReplacementCalls}</div>
          <div>Replacement Sales: {data.replacementSales}</div>
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
            <div className="italic">
              Calculation: {data.nonReplacementCalls} ÷ {data.replacementSales}{" "}
              = {data.efficiency}
            </div>
            <div className="italic">
              Lower scores indicate better performance
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const MonthlyCustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  const { theme } = useTheme();

  if (active && payload && payload.length) {
    const styles = {
      backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
      borderColor: theme === "dark" ? "#374151" : "#E5E7EB",
      color: theme === "dark" ? "#F3F4F6" : "#1F2937",
    };

    return (
      <div
        className="p-3 border rounded text-sm shadow-lg space-y-2 max-w-xs"
        style={styles}
      >
        <div className="font-semibold">Month: {label}</div>
        <div className="space-y-1">
          {payload.map((entry, index) => (
            <div className="flex items-center gap-2" key={index}>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="font-medium">{entry.name}:</span>
              <span className="font-bold">{entry.value}</span>
            </div>
          ))}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-600">
          <div className="italic">Lower scores indicate better performance</div>
        </div>
      </div>
    );
  }

  return null;
};

const NewCustomerReplacementEfficiencyChart: React.FC<Props> = ({
  initialData = [],
  monthlyData,
}) => {
  const { theme } = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>("yearly");
  const [selectedYear, setSelectedYear] = useState<number | null>(
    initialData.length > 0 ? initialData[initialData.length - 1].year : null,
  );

  const yearColors = generateColors(monthlyData?.years || [], theme);

  // Get quintiles from the most recent data or first available
  const quintiles =
    initialData.length > 0
      ? initialData[0].quintiles
      : {
          A: 0,
          B: 0,
          C: 0,
          D: 0,
          F: 0,
        };

  // Calculate Y-axis domain with padding
  const calculateYAxisDomain = () => {
    if (!initialData || initialData.length === 0) {
      return [0, 10];
    }

    const allValues = initialData.flatMap((item) => [
      item.efficiency,
      ...Object.values(item.quintiles),
    ]);

    const maxValue = Math.max(...allValues);
    const padding = maxValue * 0.1;

    return [0, Math.ceil(maxValue + padding)];
  };

  const [yMin, yMax] = calculateYAxisDomain();

  const currentYearData = selectedYear
    ? initialData.find((item) => item.year === selectedYear)
    : initialData[initialData.length - 1];

  const currentEfficiency = currentYearData?.efficiency ?? 0;

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CardTitle className="text-fontColor dark:text-light text-xl">
                New Customer Replacement Efficiency
              </CardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                    <p>
                      Measures how efficiently new market entrants convert from
                      service interactions into replacement revenue.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Quality score: new customer replacements versus service calls
            </p>
          </div>
          <div className="flex items-center sm:items-end justify-between sm:justify-end sm:flex-col gap-2">
            <SlidingToggleGroup
              onChange={(value) => setViewMode(value as ViewMode)}
              options={[
                { value: "yearly", label: "Yearly" },
                { value: "monthly", label: "Monthly" },
              ]}
              value={viewMode}
            />
            {viewMode === "yearly" && (
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {currentEfficiency}
                </div>
                {selectedYear && (
                  <div className="text-sm text-gray-500 dark:text-gray-300">
                    Year {selectedYear}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Chart */}
          <div className="w-full h-80 md:h-96">
            {viewMode === "yearly" ? (
              <ResponsiveContainer height="100%" width="100%">
                <BarChart
                  data={initialData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  onClick={(data) => {
                    if (data?.activeLabel) {
                      setSelectedYear(Number(data.activeLabel));
                    }
                  }}
                >
                  <CartesianGrid
                    stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="year"
                    stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  />
                  <YAxis
                    domain={[yMin, yMax]}
                    stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                    tickFormatter={(value) => value}
                  />
                  <ChartTooltip content={<CustomTooltip />} />

                  {/* Quintile Reference Lines */}
                  <ReferenceLine
                    stroke="#8b5cf6"
                    strokeDasharray="4 4"
                    strokeWidth={2}
                    y={quintiles.A}
                  />
                  <ReferenceLine
                    stroke="#3b82f6"
                    strokeDasharray="4 4"
                    strokeWidth={2}
                    y={quintiles.B}
                  />
                  <ReferenceLine
                    stroke="#f59e0b"
                    strokeDasharray="4 4"
                    strokeWidth={2}
                    y={quintiles.C}
                  />
                  <ReferenceLine
                    stroke="#f97316"
                    strokeDasharray="4 4"
                    strokeWidth={2}
                    y={quintiles.D}
                  />
                  <ReferenceLine
                    stroke="#ef4444"
                    strokeDasharray="4 4"
                    strokeWidth={2}
                    y={quintiles.F}
                  />

                  {/* Efficiency Bars */}
                  <Bar
                    cursor="pointer"
                    dataKey="efficiency"
                    fill="#10b981"
                    onClick={(data) => setSelectedYear(data.year)}
                    opacity={0.8}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer height="100%" width="100%">
                <BarChart
                  data={monthlyData?.items || []}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid
                    stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="month"
                    stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  />
                  <YAxis
                    stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                    tickFormatter={(value) => value}
                  />
                  <ChartTooltip content={<MonthlyCustomTooltip />} />
                  <Legend />
                  {monthlyData?.years.map((year) => (
                    <Bar
                      dataKey={year}
                      fill={yearColors[year]}
                      key={year}
                      name={year}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div
            className={`flex flex-wrap justify-center gap-4 text-sm transition-opacity ${
              viewMode === "yearly" ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-violet-500"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                ≤{quintiles.A} Top 20%
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-blue-500"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                ≤{quintiles.B} Above Avg
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-yellow-500"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                ≤{quintiles.C} Average
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-orange-500"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                ≤{quintiles.D} Below Avg
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-red-500"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                &gt;{quintiles.D} Bottom 20%
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NewCustomerReplacementEfficiencyChart;
